import { defineMessages, type IntlShape } from 'react-intl';
import type {
  BlockConfigBase,
  JSONSchema,
  SlateBlock,
  Content,
} from '@plone/types';

export interface EmbedTrekksoftData {
  '@type': 'embedtrekksoft';
  title?: string;
  description?: SlateBlock;
  full_width?: boolean;
  bg_color?: 'primary' | 'secondary' | 'tertiary';
  show_bg_image?: boolean;
  bg_image?: Content;
  cta_box_color?: string;
  cta_description?: string;
}

const schema = ({
  data,
  intl,
}: {
  data: EmbedTrekksoftData;
  intl: IntlShape;
}): JSONSchema => {
  const schema = {
    title: intl.formatMessage(messages.embed_trekksoft_title),
    fieldsets: [
      {
        id: 'default',
        title: 'Default',
        fields: [
          'title',
          'description',
          'full_width',
          'bg_color',
          'show_bg_image',
          ...(data?.show_bg_image ? ['bg_image'] : []),
          'cta_box_color',
          'cta_description',
        ],
      },
    ],
    properties: {
      title: {
        title: intl.formatMessage(messages.embed_trekksoft_title_field_title),
      },
      description: {
        title: intl.formatMessage(
          messages.embed_trekksoft_description_field_title,
        ),
        widget: 'richtext',
      },
      full_width: {
        title: intl.formatMessage(
          messages.embed_trekksoft_full_width_field_title,
        ),
        type: 'boolean',
      },
      bg_color: {
        title: intl.formatMessage(
          messages.embed_trekksoft_bg_color_field_title,
        ),
        widget: 'color_picker',
        colors: [
          {
            name: 'primary',
            label: intl.formatMessage(messages.primary),
          },
          {
            name: 'secondary',
            label: intl.formatMessage(messages.secondary),
          },
          {
            name: 'tertiary',
            label: intl.formatMessage(messages.tertiary),
          },
        ],
      },
      show_bg_image: {
        title: intl.formatMessage(
          messages.embed_trekksoft_show_bg_image_field_title,
        ),
        type: 'boolean',
      },
      bg_image: {
        title: intl.formatMessage(
          messages.embed_trekksoft_bg_image_field_title,
        ),
        widget: 'object_browser',
        mode: 'image',
        return: 'single',
      },

      cta_box_color: {
        title: intl.formatMessage(
          messages.embed_trekksoft_cta_color_field_title,
        ),
        widget: 'color_picker',
        colors: [
          {
            name: 'primary',
            label: intl.formatMessage(messages.primary),
          },
          {
            name: 'secondary',
            label: intl.formatMessage(messages.secondary),
          },
          {
            name: 'tertiary',
            label: intl.formatMessage(messages.tertiary),
          },
          {
            name: 'transparent',
            label: intl.formatMessage(messages.transparent),
          },
        ],
      },
      cta_description: {
        title: intl.formatMessage(
          messages.embed_trekksoft_cta_description_field_title,
        ),
      },
    },
    required: [...(data?.show_bg_image ? ['bg_image'] : [])],
  };

  return schema;
};

export default schema;

const messages = defineMessages({
  embed_trekksoft_title: {
    id: 'embed_trekksoft_title',
    defaultMessage: 'Embed Trekksoft',
  },
  embed_trekksoft_title_field_title: {
    id: 'embed_trekksoft_title_field_title',
    defaultMessage: 'Titolo',
  },
  embed_trekksoft_description_field_title: {
    id: 'embed_trekksoft_description_field_title',
    defaultMessage: 'Descrizione',
  },
  embed_trekksoft_full_width_field_title: {
    id: 'embed_trekksoft_full_width_field_title',
    defaultMessage: 'Mostra a pieno schermo',
  },
  embed_trekksoft_bg_color_field_title: {
    id: 'embed_trekksoft_bg_color_field_title',
    defaultMessage: 'Colore di sfondo',
  },
  embed_trekksoft_show_bg_image_field_title: {
    id: 'embed_trekksoft_show_bg_image_field_title',
    defaultMessage: 'Mostra immagine di sfondo',
  },
  embed_trekksoft_bg_image_field_title: {
    id: 'embed_trekksoft_bg_image_field_title',
    defaultMessage: 'Immagine di sfondo',
  },
  embed_trekksoft_cta_color_field_title: {
    id: 'embed_trekksoft_cta_color_field_title',
    defaultMessage: 'Colore box prenotazione',
  },
  embed_trekksoft_cta_description_field_title: {
    id: 'embed_trekksoft_cta_description_field_title',
    defaultMessage: 'Testo di introduzione bottone',
  },
  primary: {
    id: 'primary',
    defaultMessage: 'Primario',
  },
  secondary: {
    id: 'secondary',
    defaultMessage: 'Secondario',
  },
  tertiary: {
    id: 'tertiary',
    defaultMessage: 'Terziario',
  },
  transparent: {
    id: 'transparent',
    defaultMessage: 'Trasparente',
  },
});

export interface embedTrekksoftConfig
  extends Omit<BlockConfigBase, 'blockSchema'> {
  blockSchema: typeof schema;
}

// - Mostrare a larghezza schermo
// - Colore sfondo
// - Mostra immagine (sì o no)
// - Selezione immagine
// - Colore bottone
// - Testo bottone
// - Titolo
// - Descrizione
// - Descrizione bottone (es. Verifica la disponibilità e prenota)
